import restClient from "api/restClient";
import { Button } from "components/Button/Button";
import { LazyEditableCodeEditor } from "components/Form/lazyCodeEditorComponents";
import { Text } from "components/Form/components/Text/Text";
import { requiredValidator, yamlValidator } from "components/Form/Form.utils";
import { IconCollection } from "components/Icon/IconCollection";
import NotificationService from "components/Notification/Notification.service";
import RestErrorNotification from "components/Notification/RestErrorNotification";
import { BlockingNavigationModal } from "components/Prompt/BlockingNavigationModal";
import { NAVIGATION_PRIORITIES } from "components/Prompt/NavigationPromptContext";
import { SpinnerAnimation } from "components/SpinnerAnimation/SpinnerAnimation";
import { DocsInfoTooltip } from "components/Tooltip/DocsInfoTooltip";
import { useMatchingFields } from "containers/Filter/FilterBuilder/useMatchingFields";
import { FeConfigType } from "config/config";
import { actions as resourcesActions } from "containers/Resources/Resources.reducer";
import { useMigrateOnSaveParams } from "containers/Resources/screens/ResourcesSingle/useCanCommitOnSave";
import { Record_Metadata } from "graphql/generated/graphql";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Field, Form as FinalForm, FormSpy } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { actions as dataStoreAction } from "utils/DataStore/DataStore.reducer";
import strings from "utils/strings";
import { getIri, getSearchObj } from "utils/utils";
import styles from "./MetadataList.module.scss";

interface MetadataListProps {
    id: string;
    isDisabled: boolean;
    setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
    resourceName?: FeConfigType;
}

export const MetadataList = ({
    id,
    isDisabled,
    setHasUnsavedChanges,
    resourceName,
}: MetadataListProps) => {
    const dispatch = useDispatch();
    const history = useHistory();

    const key = `recordMetadataList-${id}`;

    const [isLoading, setLoading] = useState(false);

    const [formStates, setFormStates] = useState<Record<string, boolean>>({});

    useEffect(() => {
        const hasAnyDirtyForm = Object.values(formStates).some((isPristine) => !isPristine);
        setHasUnsavedChanges(hasAnyDirtyForm);
    }, [formStates, setHasUnsavedChanges]);

    const getRecordMetadataListSelector = (state) => state?.dataStore?.[key];
    const recordMetadataDataList: Record_Metadata[] = useSelector(
        getRecordMetadataListSelector
    )?.record_metadata_plain_list;

    const getData = useCallback(() => {
        setLoading(true);
        const filterStr = JSON.stringify(getSearchObj({ field: "record_id", value: id, op: "eq" }));
        new Promise((resolve, reject) => {
            dispatch({
                promise: { resolve, reject },
                type: dataStoreAction.START_FETCH_DATA,
                key,
                params: {
                    query: `query($filter: String, $order: String, $limit: Int, $offset: Int) {
                    record_metadata_plain_list(filter:$filter,order:$order,limit:$limit,offset:$offset){
                        data
                        id
                        key
                    },
                }`,
                    variables: { filter: filterStr, order: "+key", offset: 0 },
                },
            });
        })
            .then((data: any) => {
                const count = data?.record_metadata_plain_list?.length;
                dispatch({
                    type: resourcesActions.STORE_RESOURCE_DATA_WITH_PARTIAL_UPDATE,
                    iri: getIri(resourceName, id),
                    response: {
                        record_metadata_list_record_id: {
                            record_metadata_count: count,
                        },
                    },
                });
            })
            .catch((error) => error)
            .finally(() => setLoading(false));
    }, [dispatch, id, key, resourceName]);

    useEffect(() => {
        if (id) {
            getData();
        }
    }, [getData, id]);

    const [isNewForm, setNewForm] = useState(false);

    const { defaultSchemaValues } = useMatchingFields({
        activeTab: "record_metadata",
    });

    const formValues = useMemo(() => {
        const formValues = [
            ...(recordMetadataDataList ?? []),
            ...(isNewForm ? [defaultSchemaValues] : []),
        ];
        return formValues;
    }, [defaultSchemaValues, isNewForm, recordMetadataDataList]);

    const clearForm = useCallback((form, metadataId) => {
        form.reset();
        if (!metadataId) {
            setNewForm(false);
            setFormStates((prev) => ({
                ...prev,
                new: false,
            }));
        }
    }, []);

    const applyAction = (
        actiontype: string,
        actionCallback: () => Promise<any>,
        form: any,
        metadataId: string | null
    ) =>
        actionCallback()
            .then(() => {
                getData();
                clearForm(form, metadataId);
            })
            .catch((error) =>
                NotificationService.error(
                    <RestErrorNotification
                        titleOverride={`Sorry, couldn't ${actiontype} record metadata`}
                        error={error}
                    />
                )
            )
            .finally(() => setLoading(false));

    const migrateOnSaveParams = useMigrateOnSaveParams();
    const params = { commit: false, ...migrateOnSaveParams };

    return (
        <div style={{ position: "relative" }}>
            <SpinnerAnimation isLoading={isLoading} className={styles.spinnerAnimation} />
            {formValues.map((metadata) => {
                return (
                    <FinalForm
                        onSubmit={(values, form) => {
                            setLoading(true);
                            const action = `${!metadata.id ? "create" : "update"}`;
                            const createOrUpdate = () =>
                                restClient.post(
                                    `/graphql`,
                                    {
                                        query: `mutation ${action}_record_metadata($record_id: String!, $key: String!, $data: SchemalessData, ${
                                            action === "update" ? "$id: String!" : ""
                                        }) {
                            ${action}_record_metadata(body: {record_id: $record_id, key: $key, data: $data}, ${
                                            action === "update" ? "id: $id" : ""
                                        }) {
                                id
                            }
                        }`,
                                        variables: {
                                            key: values.key,
                                            data: values.data,
                                            record_id: id,
                                            id: values.id,
                                        },
                                    },
                                    { params }
                                );
                            applyAction(action, createOrUpdate, form, metadata.id);
                        }}
                        initialValues={metadata}
                        key={metadata.id}
                        keepDirtyOnReinitialize
                    >
                        {({
                            pristine,
                            form,
                            hasValidationErrors,
                            submitting,
                            handleSubmit,
                            values,
                        }) => {
                            return (
                                <div className={styles.metadataForm}>
                                    <BlockingNavigationModal
                                        navigate={history.push}
                                        shouldBlockNavigation={!pristine}
                                        titleText={strings.navigateAwayUnsavedPrompt}
                                        priority={NAVIGATION_PRIORITIES.METADATA_FORM}
                                    />
                                    <div className={styles.keyFieldContainer}>
                                        <Field name="key" id="key" validate={requiredValidator}>
                                            {({ input }) => {
                                                return (
                                                    <Text
                                                        inputProps={{
                                                            ...input,
                                                            isReadOnly: isDisabled,
                                                        }}
                                                        name="key"
                                                        attributes={{}}
                                                    />
                                                );
                                            }}
                                        </Field>
                                        <Button
                                            icon={IconCollection.deleteOutline}
                                            variation="icon"
                                            onClick={() => {
                                                if (metadata.id) {
                                                    setLoading(true);
                                                    const deleteMetadata = () =>
                                                        restClient.post(
                                                            "graphql",
                                                            {
                                                                query: `mutation delete_record_metadata($id: String!){
                                                    delete_record_metadata(id: $id)}`,
                                                                variables: {
                                                                    id: metadata.id,
                                                                },
                                                            },
                                                            { params }
                                                        );
                                                    applyAction(
                                                        "delete",
                                                        deleteMetadata,
                                                        form,
                                                        metadata.id
                                                    );
                                                } else {
                                                    clearForm(form, metadata.id);
                                                }
                                            }}
                                            disabled={isDisabled}
                                        />
                                    </div>
                                    <Field
                                        name="data"
                                        id="data"
                                        validate={yamlValidator}
                                        parse={(value) => value}
                                    >
                                        {({ input }) => {
                                            return (
                                                <LazyEditableCodeEditor
                                                    // final-form converts null to empty string, to avoid this we use the values.data
                                                    input={{ ...input, value: values.data }}
                                                    isDisabled={isDisabled}
                                                />
                                            );
                                        }}
                                    </Field>
                                    {pristine && metadata.id ? null : (
                                        <div className={styles.actionButtons}>
                                            <Button
                                                onClick={handleSubmit}
                                                label="Confirm"
                                                variation={"primary"}
                                                size={"small"}
                                                disabled={
                                                    pristine ||
                                                    hasValidationErrors ||
                                                    submitting ||
                                                    isDisabled
                                                }
                                            />
                                            <Button
                                                label="Cancel"
                                                onClick={() => {
                                                    clearForm(form, metadata.id);
                                                }}
                                                variation={"white"}
                                                size={"small"}
                                            />
                                        </div>
                                    )}
                                    <FormSpy
                                        onChange={({ pristine }) => {
                                            setTimeout(
                                                () =>
                                                    setFormStates((prev) => ({
                                                        ...prev,
                                                        [metadata.id ?? "new"]: pristine,
                                                    })),
                                                0
                                            );
                                        }}
                                    />
                                </div>
                            );
                        }}
                    </FinalForm>
                );
            })}
            <div className={styles.addMetadataContainer}>
                <Button
                    label="+ Add metadata record"
                    onClick={() => setNewForm(true)}
                    variation={"white"}
                    size={"small"}
                    disabled={isNewForm || isDisabled}
                />
                <DocsInfoTooltip
                    title="Record metadata"
                    urlPath={"/docs/accessing-and-manipulating-records#record-metadata"}
                />
            </div>
        </div>
    );
};
